import { useEffect, useState } from "react";
import { DeviceMobile, Share, DotsThreeVertical, DownloadSimple, Plus } from "@phosphor-icons/react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

export const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

export const detectPlatform = () => {
  const ua = window.navigator.userAgent;
  const iOS =
    /iphone|ipad|ipod/i.test(ua) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1); // iPadOS 13+
  if (iOS) return "ios";
  if (/android/i.test(ua)) return "android";
  return "desktop";
};

export function InstallButton({ className = "", label = "Installer l'app" }) {
  const [canInstall, setCanInstall] = useState(!!window.__deferredInstallPrompt);
  const [installed, setInstalled] = useState(isStandalone());
  const [platform, setPlatform] = useState("desktop");
  const [guideOpen, setGuideOpen] = useState(false);

  useEffect(() => {
    if (isStandalone()) {
      setInstalled(true);
      return;
    }
    setPlatform(detectPlatform());
    const onInstallable = () => setCanInstall(true);
    const onInstalled = () => {
      setInstalled(true);
      setCanInstall(false);
      setGuideOpen(false);
    };
    window.addEventListener("pwa-installable", onInstallable);
    window.addEventListener("pwa-installed", onInstalled);
    return () => {
      window.removeEventListener("pwa-installable", onInstallable);
      window.removeEventListener("pwa-installed", onInstalled);
    };
  }, []);

  if (installed) return null;

  const click = async () => {
    const dp = window.__deferredInstallPrompt;
    if (dp) {
      try {
        dp.prompt();
        const choice = await dp.userChoice;
        window.__deferredInstallPrompt = null;
        setCanInstall(false);
        if (choice && choice.outcome !== "accepted") {
          // L'utilisateur a refusé : on montre le guide manuel en repli
          setGuideOpen(true);
        }
      } catch {
        window.__deferredInstallPrompt = null;
        setCanInstall(false);
        setGuideOpen(true);
      }
      return;
    }
    // Pas d'invite native disponible (iOS, ou invite déjà consommée) → guide manuel
    setGuideOpen(true);
  };

  return (
    <>
      <button onClick={click} data-testid="install-app-btn" className={className}>
        <DeviceMobile weight="fill" /> {label}
      </button>
      <InstallGuideModal open={guideOpen} onOpenChange={setGuideOpen} platform={platform} />
    </>
  );
}

function Step({ icon, children }) {
  return (
    <li className="flex items-start gap-3">
      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-terracotta/10 text-terracotta">
        {icon}
      </span>
      <span className="pt-1 text-sm text-forest/90">{children}</span>
    </li>
  );
}

export function InstallGuideModal({ open, onOpenChange, platform }) {
  const content = {
    ios: {
      title: "Installer sur iPhone / iPad",
      desc: "Sur iOS, l'installation se fait depuis Safari en 2 étapes :",
      steps: [
        {
          icon: <Share weight="bold" className="h-4 w-4" />,
          text: (
            <>
              Appuyez sur le bouton <strong>Partager</strong> (le carré avec une flèche vers le haut,
              en bas de l'écran).
            </>
          ),
        },
        {
          icon: <Plus weight="bold" className="h-4 w-4" />,
          text: (
            <>
              Faites défiler puis choisissez <strong>« Sur l'écran d'accueil »</strong>, puis
              confirmez avec <strong>Ajouter</strong>.
            </>
          ),
        },
      ],
      note: "Astuce : cette option n'existe que dans Safari, pas dans Chrome sur iPhone.",
    },
    android: {
      title: "Installer sur Android",
      desc: "Depuis Chrome, ajoutez YAYA EXAM en quelques secondes :",
      steps: [
        {
          icon: <DotsThreeVertical weight="bold" className="h-4 w-4" />,
          text: (
            <>
              Ouvrez le menu <strong>⋮</strong> (trois points, en haut à droite).
            </>
          ),
        },
        {
          icon: <DownloadSimple weight="bold" className="h-4 w-4" />,
          text: (
            <>
              Choisissez <strong>« Installer l'application »</strong> ou{" "}
              <strong>« Ajouter à l'écran d'accueil »</strong>, puis confirmez.
            </>
          ),
        },
      ],
      note: "Si l'option n'apparaît pas, rechargez la page et réessayez.",
    },
    desktop: {
      title: "Installer sur ordinateur",
      desc: "Avec Chrome ou Edge, installez l'application en un clic :",
      steps: [
        {
          icon: <DownloadSimple weight="bold" className="h-4 w-4" />,
          text: (
            <>
              Cliquez sur l'icône <strong>d'installation</strong> (un écran avec une flèche) à
              droite de la barre d'adresse.
            </>
          ),
        },
        {
          icon: <DotsThreeVertical weight="bold" className="h-4 w-4" />,
          text: (
            <>
              Ou via le menu <strong>⋮</strong> → <strong>« Installer YAYA EXAM… »</strong>.
            </>
          ),
        },
      ],
      note: "L'application s'ouvrira alors dans sa propre fenêtre, comme un logiciel.",
    },
  };

  const c = content[platform] || content.desktop;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="install-guide-modal">
        <DialogHeader>
          <div className="mb-2 flex h-12 w-12 items-center justify-center rounded-2xl bg-terracotta text-white">
            <DeviceMobile weight="fill" className="h-6 w-6" />
          </div>
          <DialogTitle className="text-xl font-extrabold text-forest">{c.title}</DialogTitle>
          <DialogDescription className="text-forest/70">{c.desc}</DialogDescription>
        </DialogHeader>
        <ol className="mt-2 space-y-4">
          {c.steps.map((s, i) => (
            <Step key={i} icon={s.icon}>
              {s.text}
            </Step>
          ))}
        </ol>
        <p className="mt-4 rounded-xl bg-forest/5 px-4 py-3 text-xs text-forest/70">{c.note}</p>
      </DialogContent>
    </Dialog>
  );
}
